/**
 * Property Valuations API Service
 *
 * Type-safe access to property-title-admin property valuation endpoints
 * via the canonical typed client.
 *
 * Canonical spec: schema-registry/property-title-admin.openapi.yaml
 * Canonical mount: platform-admin-api on /api/v1/property/valuations
 * Migrated from contract-documentation.openapi.yaml in sprint
 * validator-cp-extensions (F-PROPERTY-VALUATIONS convergence).
 */

import { propertyTitleAdminClient } from '../client';
import type { components } from '@aaimplatform/institutional-types/property-title-admin';

type PropertyValuation = components['schemas']['PropertyValuation'];
type PropertyValuationListResponse = components['schemas']['PropertyValuationListResponse'];
type PropertyValuationRequest = components['schemas']['PropertyValuationRequest'];
type ValuationStatus = components['schemas']['ValuationStatus'];

export const propertyValuationsApi = {
  list: async (params?: {
    status?: ValuationStatus;
    tenantId?: string;
    limit?: number;
    offset?: number;
  }): Promise<PropertyValuationListResponse> => {
    const { data, error } = await propertyTitleAdminClient.GET('/property/valuations', {
      params: {
        query: {
          status: params?.status,
          tenantId: params?.tenantId,
          limit: params?.limit,
          offset: params?.offset,
        },
      },
    });
    if (error || !data) {
      const errBody = error as { code?: string; message?: string; error?: string } | undefined;
      const message = errBody?.message ?? errBody?.error ?? 'Failed to fetch property valuations';
      throw new Error(message);
    }
    return data;
  },

  create: async (body: PropertyValuationRequest): Promise<PropertyValuation> => {
    const { data, error } = await propertyTitleAdminClient.POST('/property/valuations', {
      body,
    });
    if (error || !data) {
      // Forward the structured backend error message when present.
      // Backend shapes (per platform-admin-api error middleware):
      //   { code: '...', message: '...' }      -- typed error envelope
      //   { error: '...', message: '...' }     -- legacy shape
      // Pre-customer state: ATTOM and other valuation providers may be
      // unwired in local dev, so the most common error here is
      // "ATTOM upstream unavailable -- check provider health and
      // circuit-breaker state." Surface it verbatim.
      const errBody = error as { code?: string; message?: string; error?: string } | undefined;
      const message = errBody?.message ?? errBody?.error ?? 'Failed to create property valuation';
      throw new Error(message);
    }
    return data;
  },
};
